import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Check, Clock, Landmark, MapPin, MessageSquare, Phone } from 'lucide-react';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { usePersistentState } from '../lib/usePersistentState';
import { PhoneLink } from './PhoneLink';
import { ClaimInfoHeader, FlowBadges, daysAt, type FlowRow } from './AcceptedFlowShared';
import { ClaimChat } from './ClaimChat';
import { ClaimEmailBox } from './ClaimEmailBox';
import { LorBox } from './LorBox';
import { CaseOverview } from './CaseOverview';
import { WelcomeCallBox } from './WelcomeCallBox';
import { IntakeSubmissionBox } from './IntakeSubmissionBox';
import { Module } from './ui/Module';
import { RChip } from './ui/RChip';

type Filing = {
  claim_number: string | null; insurance_company: string | null; insurance_phone: string | null; insurance_email: string | null;
  date_of_loss: string | null; claim_type: string | null; filed_at: string | null; filing_notes: string | null; lor_sent_at: string | null;
  policy_number: string | null; homeowner_name: string | null; homeowner_email: string | null; address: string | null;
};

/**
 * File with insurance: datos del filing (seguro, tipo, DOL, teléfono, claim #) + notas + LA CAJA
 * DEL LOR (LorBox: Send LOR, sello de cuándo/quién, tracker del correo, contrato, Claim filed).
 * El dueño la quiso ACÁ, en el hueco de esta sección, y no dentro del historial de emails.
 */
function FilingBox({ claimId, onFiled }: { claimId: string; onFiled: () => void }) {
  const { t } = useI18n();
  const [f, setF] = useState<Filing | null>(null);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    api.get<Filing>(`/api/claims/${claimId}/filing`).then((r) => { setF(r); setNotes(r.filing_notes ?? ''); }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimId]);

  // Las notas se guardan solas al salir del campo: antes solo se guardaban al apretar Claim filed.
  function guardarNotas() {
    if ((f?.filing_notes ?? '') === notes) return;
    api.patch(`/api/claims/${claimId}/filing`, { filingNotes: notes }).then(() => setF((x) => (x ? { ...x, filing_notes: notes } : x))).catch(() => {});
  }

  return (
    <Module id={`filing:${claimId}`} icon={Landmark} title={t('rtf.filingTitle')}
      chip={f?.lor_sent_at ? <RChip variant="ok" icon={Check}>{t('rtf.lorSent')}</RChip> : undefined}
      preview={`${f?.insurance_company || '—'} · ${f?.claim_type || '—'} · ${t('cdw.dateOfLoss')}: ${f?.date_of_loss || '—'}`}>
      <div style={{ display: 'grid', gap: 10, paddingTop: 2 }}>
      <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>
        {t('q.insurance')}: <b>{f?.insurance_company || '—'}</b> · {f?.claim_type || '—'} · {t('cdw.dateOfLoss')}: {f?.date_of_loss || '—'}
        {f?.insurance_phone && <> · <Phone size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> <PhoneLink phone={f.insurance_phone} claimId={claimId} recipientType="insurance" style={{ color: 'var(--accent)' }} /></>}
        {f?.claim_number && <> · {t('rtf.claimNumber')}: <b style={{ fontFamily: 'var(--font-mono)' }}>{f.claim_number}</b></>}
      </div>
      {/* Referencia para hacer el claim (pedido 2026-09-09): lo que el seguro pregunta al filear. */}
      <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>
        {t('cdw.policyNumber')}: <b style={{ fontFamily: 'var(--font-mono)' }}>{f?.policy_number || '—'}</b>
        {' · '}{t('claims.homeowner')}: <b>{f?.homeowner_name || '—'}</b>
        {' · '}{t('rtf.email')}: {f?.homeowner_email ? <a href={`mailto:${f.homeowner_email}`} style={{ color: 'var(--accent)' }}>{f.homeowner_email}</a> : '—'}
        {' · '}<MapPin size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {f?.address || '—'}
      </div>
      <div style={{ display: 'grid', gap: 3 }}>
        <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('rtf.notes')}</span>
        <input className="input sm" value={notes} onChange={(e) => setNotes(e.target.value)} onBlur={guardarNotas} />
      </div>
      {/* CASE OVERVIEW dentro del módulo del LOR (pedido del dueño 2026-09-09): las estrategias e
          indicaciones que el equipo escribió en Claim Review, a la vista de quien filea y manda la
          LOR, sin ir a buscarlas al claim. Es el mismo hilo (se puede escribir desde acá). */}
      <CaseOverview claimId={claimId} />
      <LorBox claimId={claimId} onFiled={onFiled} />
      </div>
    </Module>
  );
}

function ReadyRow({ r, onChanged }: { r: FlowRow; onChanged: () => void }) {
  const { t } = useI18n();
  const [open, setOpen] = usePersistentState<boolean>(`open:rtf:${r.claim_id}`, false);
  return (
    <div className="card" style={{ padding: 12 }}>
      <div role="button" tabIndex={0} onClick={() => setOpen((o) => !o)} style={{ cursor: 'pointer' }}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((o) => !o); } }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
          <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{open ? '▾' : '▸'}</span>
          <b>{r.homeowner_name || r.claim_number || `#${r.claim_id}`}</b>
          {r.claim_number && <Link to={`/claims/${r.claim_id}`} onClick={(e) => e.stopPropagation()} className="badge gray" style={{ fontSize: 'var(--fs-2xs)', fontFamily: 'var(--font-mono)', textDecoration: 'none' }}>{r.claim_number}</Link>}
          <span style={{ flex: 1 }} />
          <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{r.insurance_company || ''}</span>
          {/* CUÁNTOS DÍAS LLEVA ESPERANDO, al lado del seguro y sin abrir el cliente (pedido
              2026-08-19). Mismo cálculo que el chip de la tira de badges (daysAt sobre
              status_since): un solo número para la misma pregunta. Rojo desde los 30 días. */}
          {(() => {
            const d = daysAt(r.status_since);
            if (d == null) return null;
            const alerta = d >= 30;
            return (
              <span className="badge" title={t('rtf.daysWaitingHint')}
                style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)' as React.CSSProperties['fontWeight'], fontFamily: 'var(--font-mono)',
                  background: alerta ? 'var(--red-soft)' : 'var(--surface-2)', color: alerta ? 'var(--red)' : 'var(--muted)' }}>
                <Clock size={11} strokeWidth={2} style={{ verticalAlign: 'text-bottom' }} /> {d}d
              </span>
            );
          })()}
        </div>
        <div className="muted" style={{ fontSize: 'var(--fs-2xs)', marginTop: 4 }}>{r.contractor_name || '—'}{r.address ? <> · <MapPin size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {r.address}</> : ''}</div>
        <div style={{ marginTop: 8 }}><FlowBadges r={r} /></div>
      </div>
      {open && (
        <div style={{ borderTop: '1px solid var(--line)', marginTop: 10, paddingTop: 10, display: 'grid', gap: 12 }}>
          <ClaimInfoHeader claimId={r.claim_id} />
          {/* Grabación + respuestas de la welcome call: para filear hay que poder ESCUCHAR la
              llamada. La vista mostraba el badge verde "✓ WC" y ningún audio (reporte de
              Juan-Jared, 2026-08-03). Va en SOLO LECTURA: acá la llamada ya pasó, y el botón
              "Call with AI" volvería a llamar al homeowner y borraría esta misma grabación. */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 12, alignItems: 'start' }}>
            <FilingBox claimId={r.claim_id} onFiled={onChanged} />
            <div style={{ display: 'grid', gap: 12, alignContent: 'start' }}>
              <WelcomeCallBox claimId={r.claim_id} readOnly />
              {/* LAS PREGUNTAS DEL HOMEOWNER, al lado del LOR (pedido del dueño 2026-09-10): lo que
                  llenó en el form (y lo que el equipo completó por teléfono), con el link para ver
                  el formulario tal como lo mandó. Es el mismo módulo de Welcome Call y Claim Review. */}
              <IntakeSubmissionBox claimId={r.claim_id} defaultOpen />
            </div>
          </div>
          {/* Los updates del depto se RECOGEN (pedido 2026-09-09): en esta cola lo que se trabaja es el
              filing y el correo al seguro; el hilo largo de updates queda a un click. */}
          <Module id={`rtf-updates:${r.claim_id}`} icon={MessageSquare} title={t('rtf.schedulingUpdates')} defaultOpen={false} preview={t('rtf.schedulingUpdatesHint')}>
            <ClaimChat claimId={r.claim_id} channel="scheduling" title="Scheduling Department Updates" />
          </Module>
          <ClaimEmailBox claimId={r.claim_id} departmentKey="scheduling" defaultTab="insurance" />
          <Link to={`/claims/${r.claim_id}`} className="btn ghost sm" style={{ justifySelf: 'start' }}>{t('cdw.open')} →</Link>
        </div>
      )}
    </div>
  );
}

export function ReadyToFileSection({ rows, onChanged }: { rows: FlowRow[]; onChanged: () => void }) {
  const { t } = useI18n();
  const [ins, setIns] = useState<string>('__all__'); // filtro por aseguradora

  // Aseguradoras presentes (con conteo) para las pestañas del filtro.
  const insurers = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of rows) { const k = (r.insurance_company || '').trim() || '—'; m.set(k, (m.get(k) ?? 0) + 1); }
    return [...m.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  }, [rows]);
  /* EL MÁS VIEJO PRIMERO (pedido 2026-08-19): la cola se trabaja por antigüedad, así que el
     que lleva más días esperando encabeza. Sin fecha va al final — no se le inventa una. */
  const shown = useMemo(() => {
    const base = ins === '__all__' ? rows : rows.filter((r) => ((r.insurance_company || '').trim() || '—') === ins);
    return [...base].sort((a, b) => {
      const da = daysAt(a.status_since); const db = daysAt(b.status_since);
      if (da == null) return 1;
      if (db == null) return -1;
      return db - da;
    });
  }, [rows, ins]);

  if (rows.length === 0) return <div className="page-empty">{t('rtf.empty')}</div>;
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {/* Filtro por aseguradora: DESPLEGABLE y no pestañas (pedido 2026-08-19) — con 30
          aseguradoras los chips ocupaban cuatro renglones y empujaban la cola fuera de la
          pantalla. Ordenado por cantidad: el que más claims tiene, primero. */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <span className="muted" style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)' as React.CSSProperties['fontWeight'], textTransform: 'uppercase', letterSpacing: 0.4 }}><Building2 size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('q.insurance')}:</span>
        <select className="input sm" style={{ maxWidth: 300 }} value={ins} onChange={(e) => setIns(e.target.value)}>
          <option value="__all__">{t('rtf.allInsurers') || 'All'} ({rows.length})</option>
          {insurers.map(([name, n]) => <option key={name} value={name}>{name} ({n})</option>)}
        </select>
        {ins !== '__all__' && <button className="btn ghost sm" onClick={() => setIns('__all__')}>✕</button>}
      </div>
      {shown.map((r) => <ReadyRow key={r.presence_id} r={r} onChanged={onChanged} />)}
    </div>
  );
}
